import { CSSProperties } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from './ui';
import { useNarrow } from '../../lib/use-narrow';
import { useAppBack } from '../../lib/use-app-back';
import { useTranslation } from '../../lib/i18n';

export interface Crumb {
  label: string;
  href?: string;
}

/**
 * The desktop drill-in back affordance. On web the back button lives in the page content (this
 * slot) on desktop and in the header (BrandHeader) on mobile — so this renders a "‹ Back" button on
 * wide and nothing on narrow, where the header back button covers it. It replaced the old crumb
 * trail: the button pops history (see `useAppBack`), so the crumb `items` are no longer rendered,
 * but the prop is kept so call sites don't churn (an empty list still means "no back affordance").
 */
export function Breadcrumb({ items }: { items: Crumb[] }) {
  const narrow = useNarrow();
  const onBack = useAppBack();
  const { t } = useTranslation();
  if (narrow || items.length === 0) return null;
  return (
    <Button
      variant="text"
      size="small"
      onClick={onBack}
      startIcon={<ChevronLeft size={16} aria-hidden className="dir-flip" />}
    >
      {t('common.back')}
    </Button>
  );
}

/** The leading back-chevron icon — a left-pointing mirror of the crumb separator `Chevron`.
 *  Inherits `currentColor`, so it takes the surrounding link's color. Reused by back links that
 *  aren't full breadcrumbs (agent/task detail, settings sub-pages) to keep one chevron idiom. */
export function BackChevronIcon({ style }: { style?: CSSProperties }) {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden style={style}>
      <path
        d="M5 1L1 5L5 9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
